/**
 * CountyDetailPanel — slides in from below the map when a county is selected.
 * Designed as a placeholder/expansion point for future drilldown:
 *   county → city → postal code → courier comparison.
 */
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Truck, RotateCcw, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { COUNTY_NAMES_BY_CODE } from "./_counties_geo";

export default function CountyDetailPanel({ code, row, onClose, onFilter }) {
  return (
    <AnimatePresence>
      {code && (
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="mt-4 border border-border rounded-xl bg-white p-5"
          data-testid="county-detail-panel"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Analiză județ · <span className="font-mono">{code}</span>
                </div>
                <div className="text-xl font-bold tracking-tight">
                  {COUNTY_NAMES_BY_CODE[code] || code}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {row && row.orders > 0 && (
                <button
                  onClick={() => onFilter?.(COUNTY_NAMES_BY_CODE[code] || code)}
                  className="text-xs h-8 px-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="county-filter-btn"
                >
                  Filtrează tabelul ({row.orders})
                </button>
              )}
              <button onClick={onClose} className="h-8 w-8 rounded-md hover:bg-secondary flex items-center justify-center" data-testid="county-detail-close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {row ? (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              <Metric icon={Truck} label="Comenzi totale" value={row.orders} />
              <Metric icon={CheckCircle2} label="Livrate" value={row.delivered ?? 0} accent="emerald" />
              <Metric icon={AlertTriangle} label="Refuzate" value={row.refused ?? 0} accent="rose" />
              <Metric icon={RotateCcw} label="Retur" value={row.returned ?? 0} accent="rose" />
              <Metric
                icon={Clock}
                label="Timp tranzit"
                value={row.avg_transit_days != null ? `${Number(row.avg_transit_days).toFixed(1)} z` : "—"}
              />
              <Metric
                icon={CheckCircle2}
                label="Rată livrare"
                value={row.rate != null ? `${Number(row.rate).toFixed(1)}%` : "—"}
                accent={row.rate >= 92 ? "emerald" : row.rate >= 80 ? "amber" : "rose"}
                wide
              />
              <Metric
                icon={AlertTriangle}
                label="Rată refuz"
                value={row.refusal_rate != null ? `${Number(row.refusal_rate).toFixed(1)}%` : "—"}
                accent={row.refusal_rate <= 5 ? "emerald" : row.refusal_rate <= 12 ? "amber" : "rose"}
                wide
              />
            </div>
          ) : (
            <div className="mt-4 text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg">
              Nicio livrare în acest județ pentru perioada selectată.
            </div>
          )}

          {/* Future drilldown placeholders */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Drilldown (în curând)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <PlaceholderTile title="Top orașe" desc="Distribuție pe oraș" />
              <PlaceholderTile title="Comparator curier" desc="GLS vs Cargus vs Sameday" />
              <PlaceholderTile title="Cod poștal" desc="Heatmap fin la nivel de zonă" />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Metric({ icon: Icon, label, value, accent, wide }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    amber: "bg-amber-50 text-amber-700",
  }[accent] || "bg-secondary/60 text-foreground";
  return (
    <div className={`${wide ? "" : ""} bg-white border border-border rounded-lg p-3`}>
      <div className={`h-7 w-7 rounded-md flex items-center justify-center ${colors}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="mt-2 text-lg font-bold tabular-nums tracking-tight">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mt-0.5">{label}</div>
    </div>
  );
}

function PlaceholderTile({ title, desc }) {
  return (
    <div className="border border-dashed border-border rounded-md px-3 py-2.5 text-xs">
      <div className="font-semibold text-foreground/80">{title}</div>
      <div className="text-muted-foreground text-[11px]">{desc}</div>
    </div>
  );
}
